import { type PropsWithChildren, useContext, useMemo } from "react";

import { SafeAreaContext } from "./constants";
import type { SafeAreaContextType, SafeAreaInsets } from "./types";
import { getEmptySafeAreaContext, setSafeAreaInsets, substractSafeAreaInsets } from "./utilities";

export function SafeAreaProvider(props: PropsWithChildren) {
    const { children } = props;
    // Régler pour avoir les insets natifs
    const safeArea = useMemo<SafeAreaContextType>(() => getEmptySafeAreaContext(), []);
    return <SafeAreaContext.Provider value={safeArea}>{children}</SafeAreaContext.Provider>;
}

type SafeAreaFrameProps = PropsWithChildren<{
    insets: Partial<SafeAreaInsets>;
}>;

export function SafeAreaFrame(props: SafeAreaFrameProps) {
    const { children, insets } = props;
    const inContext = useContext(SafeAreaContext);
    const merged = useMemo(() => setSafeAreaInsets(inContext, insets), [inContext, insets]);
    return <SafeAreaContext.Provider value={merged}>{children}</SafeAreaContext.Provider>;
}

export function SafeAreaView(props: PropsWithChildren) {
    const { children } = props;
    const inContext = useContext(SafeAreaContext);
    const newContext = useMemo(
        () => substractSafeAreaInsets(inContext, inContext.current),
        [inContext],
    );
    return (
        <div
            style={{
                transition: "padding 0.3s",
                paddingTop: inContext.current.top,
                paddingBottom: inContext.current.bottom,
                paddingLeft: inContext.current.left,
                paddingRight: inContext.current.right,
            }}
        >
            <SafeAreaContext.Provider value={newContext}>{children}</SafeAreaContext.Provider>
        </div>
    );
}

type SafeAreaViewFromRootProps = PropsWithChildren<{
    top?: boolean;
    right?: boolean;
    bottom?: boolean;
    left?: boolean;
}>;

export function SafeAreaViewFromRoot(props: SafeAreaViewFromRootProps) {
    const { top = false, right = false, bottom = false, left = false } = props;
    const { children } = props;
    const inContext = useContext(SafeAreaContext);
    const newContext = useMemo(
        () => setSafeAreaInsets(inContext, inContext.cumulated),
        [inContext],
    );
    return (
        <div
            style={{
                transition: "padding 0.3s",
                paddingTop: top ? inContext.cumulated.top : 0,
                paddingBottom: bottom ? inContext.cumulated.bottom : 0,
                paddingLeft: left ? inContext.cumulated.left : 0,
                paddingRight: right ? inContext.cumulated.right : 0,
            }}
        >
            <SafeAreaContext.Provider value={newContext}>{children}</SafeAreaContext.Provider>
        </div>
    );
}
